import { isZagruProblem, type FieldError, type ZagruProblem } from '../api/problem';
import { type Translate } from './I18nProvider';

export type FailureKind =
  | 'network'
  | 'timeout'
  | 'aborted'
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'validation'
  | 'rate-limited'
  | 'unavailable'
  | 'server'
  | 'contract'
  | 'domain'
  | 'unknown';

/**
 * A failure in the operator's terms. Built from anything a request or a render can throw; never contains schema
 * paths, stack traces, exception class names or server internals. The correlation id is carried separately so a
 * screen can offer "Copy diagnostic ID" without printing it in the body.
 */
export interface UserFacingError {
  kind: FailureKind;
  title: string;
  description: string;
  /** Whether trying again can help (network, timeout, 5xx). */
  retryable: boolean;
  /** Field messages a form may place next to its inputs (validation only). */
  fieldErrors: FieldError[];
  diagnosticId?: string;
  /** The stable server code, for "Technical details". */
  code?: string;
  status?: number;
}

export interface DescribeErrorOptions {
  /** Plural noun phrase for what failed, e.g. "outbound orders", already translated. */
  subject?: string;
  /** What the user was doing. Reads decide "couldn't be loaded"; writes "couldn't be saved". */
  action?: 'load' | 'save';
}

function capitalize(text: string): string {
  return text ? text.charAt(0).toLocaleUpperCase() + text.slice(1) : text;
}

function kindOf(problem: ZagruProblem): FailureKind {
  const code = problem.code ?? '';
  if (code === 'client.aborted') return 'aborted';
  if (code === 'client.timeout') return 'timeout';
  if (code === 'client.invalid_response') return 'contract';
  if (problem.status === 0) return 'network';
  if (problem.status === 401) return 'unauthorized';
  if (problem.status === 403) return 'forbidden';
  if (problem.status === 404) return 'not-found';
  if (problem.status === 409) return 'conflict';
  if (problem.status === 429) return 'rate-limited';
  if (problem.status === 502 || problem.status === 503 || problem.status === 504 || code.endsWith('.unavailable')) return 'unavailable';
  if (problem.status >= 500) return 'server';
  if (problem.status === 400 || problem.status === 422) return problem.errors.length > 0 || code === 'validation' || code.endsWith('.validation') ? 'validation' : 'domain';
  return 'unknown';
}

/**
 * A server-provided `detail` is shown only when the server wrote it for people: a domain rule (`inventory.*`,
 * `outbound.*` …) or a validation/conflict message on a 4xx. 5xx and client-side failures never surface it.
 */
function serverSentence(problem: ZagruProblem, t: Translate): string | undefined {
  if (problem.code) {
    const key = `errors.${problem.code}`;
    const resolved = t(key, { ...problem.toJSON() });
    if (resolved !== key) return resolved;
  }
  if (problem.status >= 400 && problem.status < 500 && problem.detail && !problem.code?.startsWith('client.') && problem.status !== 401) {
    return problem.detail;
  }
  return undefined;
}

/** Maps any thrown value to copy an operator can act on. */
export function describeError(error: unknown, t: Translate, options: DescribeErrorOptions = {}): UserFacingError {
  const action = options.action ?? 'load';
  const subject = options.subject?.trim();
  const title =
    action === 'save'
      ? subject
        ? capitalize(t('failure.save.title', { subject }))
        : t('failure.save.titleGeneric')
      : subject
        ? capitalize(t('failure.load.title', { subject }))
        : t('failure.load.titleGeneric');

  if (!isZagruProblem(error)) {
    return { kind: 'unknown', title, description: t('failure.unknown'), retryable: true, fieldErrors: [] };
  }
  const problem = error;
  const kind = kindOf(problem);
  const base = { kind, title, fieldErrors: [] as FieldError[], diagnosticId: problem.correlationId, code: problem.code, status: problem.status };
  const domain = serverSentence(problem, t);
  switch (kind) {
    case 'network':
      return { ...base, description: t('failure.network'), retryable: true };
    case 'timeout':
      return { ...base, description: t('failure.timeout'), retryable: true };
    case 'aborted':
      return { ...base, description: t('failure.unknown'), retryable: true };
    case 'unauthorized':
      return { ...base, description: t('failure.unauthorized'), retryable: false };
    case 'forbidden':
      return { ...base, description: domain ?? t('failure.forbidden'), retryable: false };
    case 'not-found':
      return { ...base, description: t('failure.notFound'), retryable: false };
    case 'conflict':
      return { ...base, description: domain ?? t('failure.conflict'), retryable: false };
    case 'rate-limited':
      return { ...base, description: t('failure.rateLimited'), retryable: true };
    case 'validation':
      return {
        ...base,
        description: domain && problem.errors.length === 0 ? domain : t('failure.validation'),
        retryable: false,
        fieldErrors: problem.errors.filter((e) => e.message),
      };
    case 'domain':
      return { ...base, description: domain ?? t('failure.unknown'), retryable: false };
    case 'unavailable':
      return { ...base, description: t('failure.unavailable'), retryable: true };
    case 'contract':
      return { ...base, description: t('failure.contract'), retryable: true };
    case 'server':
      return {
        ...base,
        description:
          // Z-01 (W3): a language whose sentence opens with the subject (tr "{subject} alınamadı.") gets a capital, as the title does.
          action === 'save' ? t('failure.server.save') : subject ? capitalize(t('failure.server.load', { subject })) : t('failure.server.loadGeneric'),
        retryable: true,
      };
    default:
      return { ...base, description: domain ?? t('failure.unknown'), retryable: true };
  }
}
